import React, { useState, useRef, useEffect } from "react";
import { Link, router } from "@inertiajs/react";
import BlogPreviewCarousel from "@/Components/Carousels/BlogPreviewCarousel";

const BlogCardsItemOne = ({
    blogId,
    userSrc,
    images,
    name,
    userName,
    date,
    cardTitle,
    cardContent,
    blogUrl,
    currentUserIsOwner = false,
    draft,
}) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [removed, setRemoved] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown on outside click
    useEffect(() => {
        function handleClickOutside(event) {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setDropdownOpen(false);
            }
        }
        if (dropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownOpen]);

    const handleDelete = async () => {
        if (!window.confirm("Are you sure you want to delete this blog?"))
            return;
        setDeleting(true);
        router.delete(route("users.blogs.destroy", { id: blogId }), {
            onFinish: () => {
                setDeleting(false);
                setDropdownOpen(false);
            },
            onSuccess: () => {
                setRemoved(true);
            },
            onError: () => {
                alert("Failed to delete blog.");
            },
            preserveScroll: true,
            only: ["blogs"],
        });
    };

    if (removed) return null;

    return (
        <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark relative">
            {/* Draft Ribbon */}
            {draft && (
                <div className="absolute top-0 right-0 z-20">
                    <span
                        className="absolute top-0 right-0 bg-rose-600 text-white text-xs font-medium px-2 py-0.5 rounded-bl-md shadow-sm tracking-wide"
                        title="This blog is a draft"
                    >
                        Draft
                    </span>
                </div>
            )}
            {/* 3 dots dropdown */}
            {currentUserIsOwner && (
                <div className="absolute top-4 right-4 z-10" ref={dropdownRef}>
                    <button
                        onClick={() => setDropdownOpen((v) => !v)}
                        className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none"
                        aria-label="More options"
                        type="button"
                    >
                        <svg
                            width="20"
                            height="20"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <circle cx="4" cy="10" r="2" />
                            <circle cx="10" cy="10" r="2" />
                            <circle cx="16" cy="10" r="2" />
                        </svg>
                    </button>
                    {dropdownOpen && (
                        <div className="absolute right-0 mt-2 w-32 rounded-md shadow-lg bg-white dark:bg-boxdark ring-1 ring-black ring-opacity-5 z-20">
                            <div className="py-1">
                                <Link
                                    href={`/blogs/create?draft_id=${blogId}`}
                                    className="block px-4 py-2 text-sm text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                    Edit
                                </Link>
                                <button
                                    onClick={handleDelete}
                                    disabled={deleting}
                                    className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                    {deleting ? "Deleting..." : "Delete"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
            <Link href={`/users/${userName}`}>
                <div className="flex items-center gap-3 py-5 px-6">
                    <div className="h-10 w-10 rounded-full">
                        <img
                            src={userSrc}
                            alt="User"
                            className="rounded-full"
                        />
                    </div>
                    <div>
                        <h4 className="font-medium text-black dark:text-white">
                            {name}
                        </h4>
                        <p className="text-sm">{date}</p>
                    </div>
                </div>
            </Link>
            <Link
                href={
                    draft
                        ? `/blogs/create?draft_id=${blogId}`
                        : `/blogs/${blogUrl}`
                }
                className="block px-4"
            >
                <BlogPreviewCarousel images={images} />
            </Link>

            <div className="p-6">
                <h4 className="mb-3 text-xl font-semibold text-black hover:text-primary dark:text-white dark:hover:text-primary">
                    <Link
                        href={
                            draft
                                ? `/blogs/create?draft_id=${blogId}`
                                : `/blogs/${blogUrl}`
                        }
                    >
                        {cardTitle.length > 58
                            ? `${cardTitle.slice(0, 58)}...`
                            : cardTitle}
                    </Link>
                </h4>
                <p>
                    {(cardContent || "").length > 118
                        ? `${cardContent.slice(0, 118)}...`
                        : cardContent}
                </p>
            </div>
        </div>
    );
};

export default BlogCardsItemOne;
